import { useQueryClient } from "@tanstack/react-query";
import {
  eachDayOfInterval,
  format,
  isSaturday,
  isSunday,
  parseISO,
} from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { z } from "zod";

import { MenuItem } from "@luna-restaurant/feature-luna-school-menu-plan/build/components/MenuCard/MenuItem";
import { MenuItem as MenuItemType } from "@luna-restaurant/feature-luna-school-menu-plan/build/components/MenuCard/types";
import {
  FormDatePicker,
  FormError,
  FormProvider,
  FormTextInput,
  SubmitButton,
  useFormWrapper,
} from "@luna-restaurant/forms";
import { useTranslation } from "@luna-restaurant/i18n";
import type {
  SchoolGuestOrder,
  SchoolGuestOrderPayload,
} from "@luna-restaurant/school-api";
import {
  Button,
  NoticeBox,
  ThemedLoader,
  ThemedText,
  useCustomTheme,
} from "@luna-restaurant/ui";

import { SecondaryButton } from "@/components/SecondaryButton";
import { GuestOrderMenuQuantityInput } from "@/components/forms/GuestOrderMenuQuantityInput";
import {
  GUEST_ORDER_AMOUNT_COLUMN_SEPARATOR_OFFSET,
  GUEST_ORDER_AMOUNT_COLUMN_WIDTH,
} from "@/components/forms/guestOrderMenuLayout";
import { clampGuestOrderMenuQuantity } from "@/components/forms/guestOrderMenuQuantity";
import { useClosingTimes } from "@/hooks/useClosingTimes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useGuestOrderMealPlan } from "@/hooks/useGuestOrderMealPlan";
import { useRequest } from "@/hooks/useRequest";
import {
  getEarliestOrderableDate,
  isClosedDay,
  isOrderableDate,
} from "@/utils/guestOrderDeadline";

const POSITION_TO_MENU_CODE: Record<number, string> = {
  0: "A",
  1: "B",
  2: "C",
  3: "D",
};

const formatDayDate = (
  day: { dayNumber: number; month?: number },
  dateFrom: string,
  language: string,
): string => {
  const from = parseISO(dateFrom);
  const fromYear = from.getFullYear();
  const month = day.month ?? 0;
  const year = month < from.getMonth() ? fromYear + 1 : fromYear;
  return new Date(year, month, day.dayNumber).toLocaleDateString(language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const createNewGuestOrderSchema = (t: ReturnType<typeof useTranslation>["t"]) =>
  z
    .object({
      editor: z
        .string()
        .min(1, t("guestOrders.form.editorNameIsNeeded"))
        .max(255, t("guestOrders.form.editorNameTooLong"))
        .refine((val) => !/\d/.test(val), {
          error: t("guestOrders.form.editorNameCannotContainNumbers"),
        }),
      dateFrom: z.string().min(1, t("guestOrders.form.dateFromIsNeeded")),
      dateTo: z.string().min(1, t("guestOrders.form.dateToIsNeeded")),
      comment: z
        .string()
        .max(1000, t("guestOrders.form.commentTooLong"))
        .optional(),
    })
    .refine(
      (data) => !data.dateFrom || !data.dateTo || data.dateTo >= data.dateFrom,
      {
        message: t("guestOrders.form.dateToMustBeAfterDateFrom"),
        path: ["dateTo"],
      },
    );

export type NewGuestOrderFormValues = z.infer<
  ReturnType<typeof createNewGuestOrderSchema>
>;

type NewGuestOrderFormProps = {
  onSubmitSuccess?: (
    values: NewGuestOrderFormValues,
    info: { skippedDates: string[] },
  ) => void;
  onSubmitError?: (error: unknown) => void;
};

export function NewGuestOrderForm({
  onSubmitSuccess,
  onSubmitError,
}: NewGuestOrderFormProps = {}) {
  const { t, i18n } = useTranslation();
  const { colors, fonts } = useCustomTheme();
  const request = useRequest();
  const queryClient = useQueryClient();
  const { currentUser } = useCurrentUser();
  const [selectedMenus, setSelectedMenus] = useState<Record<string, number>>(
    {},
  );
  const [formIsLocked, setFormIsLocked] = useState<boolean>(false);
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const [missingSelectionError, setMissingSelectionError] = useState(false);

  // Only public holidays matter for guest orders (supplier is not affected by school closings)
  const { items: closingTimes } = useClosingTimes({
    type: "PUBLIC_HOLIDAY",
    fetchAllPages: true,
  });

  const isNonBusinessDay = useCallback(
    (date: Date) =>
      isSaturday(date) || isSunday(date) || isClosedDay(date, closingTimes),
    [closingTimes],
  );

  const onSetMenu = (
    id: MenuItemType["id"],
    direction: "plus" | "minus" = "plus",
  ) => {
    setSelectedMenus((prev) => {
      const menuCount = prev[id] ?? 0;
      const delta = direction === "plus" ? 1 : -1;
      const next = clampGuestOrderMenuQuantity(menuCount + delta);
      const nextMenus: Record<string, number> = { ...prev, [id]: next };

      if (missingSelectionError && orderableMealPlan) {
        const allDaysHaveSelection = orderableMealPlan.every((day) =>
          day.menus.some((menu) => (nextMenus[String(menu.id)] ?? 0) > 0),
        );
        if (allDaysHaveSelection) {
          setMissingSelectionError(false);
          form.clearErrors("root");
        }
      }

      return nextMenus;
    });
  };

  const onSetMenuCount = (id: MenuItemType["id"], nextCount: number) => {
    setSelectedMenus((prev) => {
      const nextMenus: Record<string, number> = {
        ...prev,
        [id]: clampGuestOrderMenuQuantity(nextCount),
      };

      if (missingSelectionError && orderableMealPlan) {
        const allDaysHaveSelection = orderableMealPlan.every((day) =>
          day.menus.some((menu) => (nextMenus[String(menu.id)] ?? 0) > 0),
        );
        if (allDaysHaveSelection) {
          setMissingSelectionError(false);
          form.clearErrors("root");
        }
      }

      return nextMenus;
    });
  };

  const NewGuestOrderSchema = createNewGuestOrderSchema(t);

  const form = useFormWrapper<typeof NewGuestOrderSchema>({
    schema: NewGuestOrderSchema,
    defaultValues: {
      editor: "",
      dateFrom: "",
      dateTo: "",
      comment: "",
    },
    mode: "onSubmit",
    rethrowSubmitError: true,
  });

  const dateFrom = form.watch("dateFrom");
  const dateTo = form.watch("dateTo");
  const { mealPlan, isLoading } = useGuestOrderMealPlan({
    dateFrom,
    dateTo,
    enabled: shouldFetch && !!dateFrom && !!dateTo,
  });

  const now = new Date();
  const earliestOrderableDate = getEarliestOrderableDate(now, closingTimes);
  const hasEarliestOrderableDate = !Number.isNaN(
    earliestOrderableDate.getTime(),
  );
  const orderableMealPlan = mealPlan
    ? mealPlan.filter((day) => {
        const from = parseISO(dateFrom);
        const month = day.month ?? 0;
        const year =
          month < from.getMonth() ? from.getFullYear() + 1 : from.getFullYear();
        const dayDate = new Date(year, month, day.dayNumber);
        if (isSaturday(dayDate) || isSunday(dayDate)) return false;
        return isOrderableDate(dayDate, now, closingTimes);
      })
    : null;

  const interleavedItems = useMemo(() => {
    if (!dateFrom || !dateTo) return [];
    const start = parseISO(dateFrom);
    const end = parseISO(dateTo);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
      return [];
    }

    const orderableByKey = new Map(
      (orderableMealPlan ?? []).map((day) => [day.dateKey, day]),
    );
    const businessDays = eachDayOfInterval({ start, end }).filter(
      (d) => !isNonBusinessDay(d),
    );

    type OrderableDay = NonNullable<typeof orderableMealPlan>[number];
    const items: (
      | { type: "day"; day: OrderableDay }
      | { type: "gap"; start: Date; end: Date }
    )[] = [];
    let gapStart: Date | null = null;
    let gapEnd: Date | null = null;

    const flushGap = () => {
      if (gapStart && gapEnd) {
        items.push({ type: "gap", start: gapStart, end: gapEnd });
      }
      gapStart = null;
      gapEnd = null;
    };

    for (const day of businessDays) {
      const match = orderableByKey.get(format(day, "yyyy-MM-dd"));
      if (match) {
        flushGap();
        items.push({ type: "day", day: match });
      } else {
        if (!gapStart) gapStart = day;
        gapEnd = day;
      }
    }
    flushGap();

    return items;
  }, [orderableMealPlan, dateFrom, dateTo, isNonBusinessDay]);

  const lockForm = async () => {
    const valid = await form.trigger();
    if (!valid) return;
    setShouldFetch(true);
    setFormIsLocked(true);
  };

  const buildPayloads = (
    values: NewGuestOrderFormValues,
  ): { payloads: SchoolGuestOrderPayload[]; skippedDates: string[] } => {
    const submitNow = new Date();
    const base = {
      ...(values.comment ? { comment: values.comment } : {}),
      editedBy: values.editor,
    };

    if (orderableMealPlan?.length) {
      const payloads: SchoolGuestOrderPayload[] = [];
      const skippedDates: string[] = [];
      for (const day of orderableMealPlan) {
        const dayDate = parseISO(day.dateKey);
        if (!isOrderableDate(dayDate, submitNow, closingTimes)) {
          skippedDates.push(format(dayDate, "P"));
          continue;
        }

        const positions = day.menus
          .filter((menu) => (selectedMenus[menu.id] ?? 0) > 0)
          .filter((menu) => menu.position in POSITION_TO_MENU_CODE)
          .map((menu) => ({
            menuCode: POSITION_TO_MENU_CODE[menu.position],
            quantity: selectedMenus[menu.id],
          }));
        if (positions.length > 0) {
          payloads.push({
            ...base,
            positions,
            servingAt: day.dateKey as SchoolGuestOrderPayload["servingAt"],
          });
        }
      }
      return { payloads, skippedDates };
    }

    if (mealPlan?.length) {
      return {
        payloads: [],
        skippedDates: mealPlan.map((d) => format(parseISO(d.dateKey), "P")),
      };
    }
    return { payloads: [], skippedDates: [] };
  };

  const hasMissingDaySelections = useCallback(() => {
    if (!orderableMealPlan?.length) return false;
    return orderableMealPlan.some(
      (day) => !day.menus.some((menu) => (selectedMenus[menu.id] ?? 0) > 0),
    );
  }, [orderableMealPlan, selectedMenus]);

  const onSubmit: Parameters<typeof form.submit>[0] = async ({ values }) => {
    const schoolPath = currentUser?.school;
    if (!schoolPath) {
      form.setError("root", {
        message: t("guestOrders.form.noSchoolAssigned"),
      });
      return;
    }

    if (orderableMealPlan?.length && hasMissingDaySelections()) {
      setMissingSelectionError(true);
      form.setError("root", {
        message: t("guestOrders.form.everyDayNeedsSelection"),
      });
      return;
    }
    setMissingSelectionError(false);

    const { payloads, skippedDates } = buildPayloads(values);
    if (payloads.length === 0) {
      form.setError("root", {
        message:
          skippedDates.length > 0
            ? t("guestOrders.form.datesNoLongerOrderable")
            : t("guestOrders.form.noPositionsSelected"),
      });
      return;
    }

    try {
      for (const payload of payloads) {
        await request<SchoolGuestOrder>(`${schoolPath}/guest-orders`, {
          method: "POST",
          headers: { "Content-Type": "application/ld+json" },
          body: JSON.stringify(payload),
        });
      }

      onSubmitSuccess?.(values, { skippedDates });
      // useFormWrapper auto-resets form values on successful submit,
      // so we must also reset local state to stay in sync
      setSelectedMenus({});
      setFormIsLocked(false);
      setShouldFetch(false);
      setMissingSelectionError(false);
    } catch (error) {
      onSubmitError?.(error);
      throw error;
    } finally {
      await queryClient.invalidateQueries({
        queryKey: ["guest-orders", schoolPath],
      });
    }
  };

  const resetForm = () => {
    setSelectedMenus({});
    setFormIsLocked(false);
    setShouldFetch(false);
    setMissingSelectionError(false);
    form.reset();
  };

  return (
    <FormProvider form={form}>
      <View style={styles.formContainer}>
        <View style={styles.formElement}>
          <ThemedText>{t("guestOrders.form.dateLabel")}*</ThemedText>
          <View style={styles.formContainer}>
            <View style={styles.formElement}>
              <FormDatePicker
                name="dateFrom"
                placeholder={t("guestOrders.form.dateFromPlaceholder")}
                minDate={
                  hasEarliestOrderableDate ? earliestOrderableDate : undefined
                }
                maxDate={dateTo ? parseISO(dateTo) : undefined}
                isDateDisabled={isNonBusinessDay}
                disabled={formIsLocked || !hasEarliestOrderableDate}
              />
            </View>
            <View style={styles.formElement}>
              <FormDatePicker
                name="dateTo"
                placeholder={t("guestOrders.form.dateToPlaceholder")}
                minDate={
                  dateFrom
                    ? parseISO(dateFrom)
                    : hasEarliestOrderableDate
                      ? earliestOrderableDate
                      : undefined
                }
                initialMonth={dateFrom ? parseISO(dateFrom) : undefined}
                isDateDisabled={isNonBusinessDay}
                disabled={formIsLocked || !hasEarliestOrderableDate}
              />
            </View>
          </View>
          <ThemedText>{t("guestOrders.form.editorLabel")}*</ThemedText>
          <FormTextInput
            name="editor"
            icon={{
              iconSet: "material-community",
              icon: "account-circle-outline",
            }}
            label={t("guestOrders.form.editorPlaceholder")}
          />
          <ThemedText>{t("guestOrders.form.commentLabel")}</ThemedText>
          <FormTextInput
            multiline
            icon={{ iconSet: "material-community", icon: "playlist-edit" }}
            name="comment"
            label={t("guestOrders.form.commentPlaceholder")}
          />
          <NoticeBox
            title={t("common.notice")}
            textContent={t("guestOrders.createGuestOrderNotice")}
          />
          <Text style={[styles.requiredFieldsHint, fonts.regular]}>
            {t("common.form.requiredFieldsHint")}
          </Text>
          <View style={styles.buttonContainer}>
            <SecondaryButton
              label={t("guestOrders.form.resetButton")}
              iconName="delete-outline"
              iconPosition="left"
              onPress={resetForm}
            />

            {!formIsLocked && (
              <SubmitButton
                title={t("guestOrders.form.chooseDishes")}
                onPress={lockForm}
                buttonStyle={[
                  styles.submitButton,
                  { backgroundColor: colors.border },
                ]}
                buttonTextStyle={[
                  styles.buttonText,
                  { color: colors.background, ...fonts.bold },
                ]}
                iconName="arrow-right"
                iconPosition="right"
                iconColor={colors.background}
              />
            )}
          </View>
        </View>
        <View style={styles.formElement}>
          {formIsLocked && (
            <>
              <ThemedText>
                {t("guestOrders.form.pleaseChooseDishes")}*
              </ThemedText>
              {isLoading ? (
                <ThemedLoader spinnerSize={50} />
              ) : interleavedItems.length > 0 ? (
                <View style={styles.gap16}>
                  {interleavedItems.map((item) => {
                    if (item.type === "gap") {
                      const isSingleDay =
                        format(item.start, "yyyy-MM-dd") ===
                        format(item.end, "yyyy-MM-dd");
                      return (
                        <NoticeBox
                          key={`gap-${format(item.start, "yyyy-MM-dd")}`}
                          title={t("common.notice")}
                          textContent={
                            isSingleDay
                              ? t("guestOrders.noMealPlanForSingleDateNotice", {
                                  date: item.start.toLocaleDateString(
                                    i18n.language,
                                    {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    },
                                  ),
                                })
                              : t("guestOrders.noMealPlanForDatesNotice", {
                                  dateFrom: item.start.toLocaleDateString(
                                    i18n.language,
                                    {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    },
                                  ),
                                  dateTo: item.end.toLocaleDateString(
                                    i18n.language,
                                    {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    },
                                  ),
                                })
                          }
                        />
                      );
                    }
                    const { day } = item;
                    const dayHasSelection = day.menus.some(
                      (menu) => (selectedMenus[menu.id] ?? 0) > 0,
                    );
                    const showDayError =
                      missingSelectionError && !dayHasSelection;
                    return (
                      <View
                        key={day.dateKey}
                        style={[
                          styles.dayCard,
                          {
                            outlineColor: showDayError
                              ? colors.danger
                              : colors.mutedBorder,
                          },
                          showDayError && styles.dayCardError,
                        ]}
                      >
                        <View
                          style={[
                            styles.daySeparator,
                            { backgroundColor: colors.mutedBorder },
                          ]}
                        />
                        <View style={styles.rowCentered}>
                          <ThemedText type="headline" style={styles.flexGrow}>
                            {formatDayDate(day, dateFrom, i18n.language)}
                          </ThemedText>
                          <ThemedText
                            type="headline"
                            style={[styles.amountColumn, fonts.regular]}
                          >
                            {t("common.amount")}
                          </ThemedText>
                        </View>

                        <View style={styles.gap16}>
                          {day.menus.map((menu) => (
                            <View key={menu.id} style={styles.menuRow}>
                              <View style={styles.menuItemContainer}>
                                <MenuItem item={menu} />
                              </View>
                              <View style={styles.counterRow}>
                                <Button
                                  buttonStyle={[
                                    styles.squareIconButton,
                                    { borderColor: colors.mutedBorder },
                                  ]}
                                  iconName="minus"
                                  onPress={() => onSetMenu(menu.id, "minus")}
                                />
                                <GuestOrderMenuQuantityInput
                                  value={selectedMenus[menu.id] ?? 0}
                                  onChange={(value) =>
                                    onSetMenuCount(menu.id, value)
                                  }
                                />
                                <Button
                                  buttonStyle={[
                                    styles.squareIconButton,
                                    { borderColor: colors.mutedBorder },
                                  ]}
                                  iconName="plus"
                                  onPress={() => onSetMenu(menu.id, "plus")}
                                />
                              </View>
                            </View>
                          ))}
                        </View>
                      </View>
                    );
                  })}
                  <View></View>
                </View>
              ) : (
                <NoticeBox
                  title={t("common.notice")}
                  textContent={t("guestOrders.noMealPlanNotice")}
                />
              )}

              <FormError />
              <View style={[styles.buttonContainer, styles.justifyEnd]}>
                <SubmitButton
                  title={t("guestOrders.form.orderGuestOrders")}
                  onPress={() => {
                    void form.submit(onSubmit).catch(() => undefined);
                  }}
                  buttonStyle={[
                    styles.submitButton,
                    { backgroundColor: colors.border },
                  ]}
                  buttonTextStyle={[
                    styles.buttonText,
                    { color: colors.background, ...fonts.bold },
                  ]}
                  iconName="arrow-right"
                  iconPosition="right"
                  iconColor={colors.background}
                  disabled={
                    form.formState.isSubmitting ||
                    isLoading ||
                    !orderableMealPlan?.length
                  }
                />
              </View>
            </>
          )}
        </View>
      </View>
    </FormProvider>
  );
}

const styles = StyleSheet.create({
  submitButton: {
    borderRadius: 56,
    height: 56,
    gap: 0,
    paddingHorizontal: 16,
  },
  buttonText: {
    fontSize: 16,
    paddingHorizontal: 8,
  },
  formContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    gap: 32,
  },
  formElement: {
    flex: 1,
    gap: 16,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  justifyEnd: {
    justifyContent: "flex-end",
  },
  gap16: {
    gap: 16,
  },
  dayCardError: {
    outlineWidth: 2,
    outlineOffset: -1,
  },
  dayCard: {
    flexDirection: "column",
    gap: 16,
    outlineWidth: 1,
    outlineStyle: "solid",
    padding: 16,
    borderRadius: 16,
    marginBottom: 16,
    position: "relative",
  },
  daySeparator: {
    position: "absolute",
    top: 0,
    bottom: 0,
    right: GUEST_ORDER_AMOUNT_COLUMN_SEPARATOR_OFFSET,
    width: 1,
  },
  rowCentered: {
    flexDirection: "row",
    gap: 16,
    alignItems: "center",
  },
  flexGrow: {
    flexGrow: 1,
  },
  amountColumn: {
    width: GUEST_ORDER_AMOUNT_COLUMN_WIDTH,
  },
  menuRow: {
    flexDirection: "row",
    gap: 16,
    alignItems: "center",
    justifyContent: "space-between",
  },
  menuItemContainer: {
    flexGrow: 1,
    paddingRight: 16,
    flexShrink: 1,
    minWidth: 0,
  },
  counterRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
  },
  squareIconButton: {
    borderWidth: 1,
    paddingHorizontal: 0,
    justifyContent: "center",
    height: 44,
    width: 44,
    minWidth: 44,
  },
  requiredFieldsHint: {
    marginTop: -6,
    marginBottom: 16,
    fontSize: 16,
    lineHeight: 20.8,
  },
});
